import { InfoOutlined } from '@mui/icons-material';
import { Box, Link, Tooltip, Typography, useTheme } from '@mui/material';
import type { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type Props = {
  budgetPercentage: number | null;
  plantId: string;
  budget: string | null;
};

export const BudgetUtilizationBar: FC<Props> = ({ budgetPercentage, plantId, budget }) => {
  const theme = useTheme();

  if (budget === null) {
    return (
      <Box
        sx={{
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: '8px',
          backgroundColor: theme.palette.SlabGray[100],
        }}
      >
        <Box display='flex' alignItems='center' gap={1}>
          <InfoOutlined sx={{ fontSize: 16, color: theme.palette.SlabGray[600] }} />
          <Typography variant='body2' color={theme.palette.SlabGray[600]}>
            No budget set{' '}
          </Typography>
        </Box>

        <Link
          component={RouterLink}
          to={`/plants/${plantId}`}
          variant='body2'
          sx={{
            fontWeight: 500,
            textDecorationColor: 'transparent',
            '&:hover': {
              textDecorationColor: theme.palette.SlabIndigo[700],
            },
          }}
        >
          Set to unlock metric
        </Link>
      </Box>
    );
  }

  if (budgetPercentage === null) {
    return null;
  }

  const isOverTarget = budgetPercentage >= 100;
  const barWidth = Math.min(budgetPercentage, 100);
  const label = isOverTarget
    ? `Over target: ${budgetPercentage}%`
    : `Under target: ${budgetPercentage}%`;

  const barColor = isOverTarget ? theme.palette.SlabRed[500] : theme.palette.SlabGreen[600];
  const labelBg = isOverTarget ? theme.palette.SlabRed[100] : theme.palette.SlabGreen[100];
  const labelColor = isOverTarget ? theme.palette.SlabRed[500] : theme.palette.SlabGreen[600];

  const tooltipText = `Percentage of budget filled by sold volume (quotes pushed to dispatch). Sold ÷ Budget × 100. "Under target" means sold volume is below budget, "Over target" means sold exceeds budget. Budget is tracked per location, so this compares all sold volume measured in the same unit against the location's budget.`;
  return (
    <Tooltip title={tooltipText}>
      <Box
        sx={{
          backgroundColor: labelBg,
          p: 1.5,
          borderRadius: '8px',
        }}
      >
        <Typography variant='body2' color={labelColor} pb={0.5}>
          {label}
        </Typography>

        <Box
          sx={{
            width: '100%',
            height: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.SlabGray[300],
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${barWidth}%`,
              height: '100%',
              borderRadius: 3,
              backgroundColor: barColor,
              transition: 'width 0.4s ease',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};
